/**
 * FlowFunnelCanvas - Canvas React Flow read-only com métricas de funil
 * 
 * Renderiza os nodes e edges do fluxo em modo somente-leitura,
 * sobrepondo métricas de conversão e drop-off em tempo real.
 * Reutiliza os mesmos componentes de node do fluxo principal.
 * 
 * @release v2.1.0 - 2026-10-02 - Usa catálogo visual único e corrige o node Conexão.
 * @release v2.0.0 - 2026-03-07
 */

import { useMemo, useCallback, useState, type MouseEvent } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  NodeTypes,
  EdgeTypes,
  Panel,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useQuery } from '@tanstack/react-query';
import { CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Check, TrendingDown, Clock, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useFlowFunnelStats } from '@/hooks/useFlowFunnelStats';
import { useABTestStats } from '@/hooks/useABTestStats';
import { createFunnelWrapper } from './FunnelNodeOverlay';
import { FunnelEdge } from './FunnelEdgeLabel';
import { FunnelNodeDrilldownModal } from './FunnelNodeDrilldownModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { createLogger } from '@/lib/logger';
import { FLOW_NODE_COMPONENTS } from './nodes/flowNodeRegistry';

const log = createLogger('FlowFunnelCanvas');

/** Opções de período */
const PERIOD_OPTIONS = [
  { value: '24h', label: 'Últimas 24 horas', short: '24h' },
  { value: '7d', label: 'Últimos 7 dias', short: '7 dias' },
  { value: '30d', label: 'Últimos 30 dias', short: '30 dias' },
  { value: '90d', label: 'Últimos 90 dias', short: '90 dias' },
];

interface FlowFunnelCanvasProps {
  /** ID do fluxo */
  flowId: string;
}

/**
 * Componente principal do Funil Tempo Real
 */
export function FlowFunnelCanvas({ flowId }: FlowFunnelCanvasProps) {
  const [period, setPeriod] = useState('7d');
  const [periodOpen, setPeriodOpen] = useState(false);
  /** Estado do painel lateral - retraído por padrão */
  const [insightsOpen, setInsightsOpen] = useState(false);
  /** Estado do drill-down: node selecionado para ver sessões */
  const [drilldownNodeId, setDrilldownNodeId] = useState<string | null>(null);
  const [drilldownNodeLabel, setDrilldownNodeLabel] = useState('');
  const [drilldownEntries, setDrilldownEntries] = useState(0);

  // Busca a estrutura do fluxo (nodes e edges originais)
  const { data: flowData, isLoading: isLoadingFlow } = useQuery({
    queryKey: ['flow-structure', flowId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flows')
        .select('nodes, edges')
        .eq('id', flowId)
        .single();

      if (error) throw error;
      return data as { nodes: any[]; edges: any[] };
    },
    enabled: !!flowId,
  });

  // Estatísticas do funil via RPC + realtime
  const {
    nodeStats,
    edgeStats,
    getNodeStats,
    getEdgeStats,
    isLoading: isLoadingStats,
  } = useFlowFunnelStats(flowId, period);

  // Estatísticas de A/B Test (mesmo hook do fluxo principal)
  const { stats: abTestStats } = useABTestStats(flowId);

  /** Deriva todos os tipos do catálogo do construtor e adiciona métricas. */
  const nodeTypes: NodeTypes = useMemo(
    () => Object.fromEntries(
      Object.entries(FLOW_NODE_COMPONENTS).map(([type, Component]) => [
        type,
        createFunnelWrapper(Component),
      ]),
    ) as NodeTypes,
    [],
  );

  // Tipo de edge: funil
  const edgeTypes: EdgeTypes = useMemo(
    () => ({
      default: FunnelEdge,
      abtest: FunnelEdge,
    }),
    [],
  );

  // Nodes com dados de stats injetados
  const nodesWithStats = useMemo(() => {
    if (!flowData?.nodes) return [];
    return flowData.nodes.map((node: any) => ({
      ...node,
      data: {
        ...node.data,
        funnelStats: getNodeStats(node.id),
      },
      // Desabilita interação
      draggable: false,
      selectable: false,
      connectable: false,
    }));
  }, [flowData?.nodes, getNodeStats]);

  // Edges com dados de stats injetados (funil + A/B test)
  const edgesWithStats = useMemo(() => {
    if (!flowData?.edges) return [];
    return flowData.edges.map((edge: any) => {
      // Verifica se a edge sai de um node ab_test
      const sourceNode = flowData.nodes?.find((n: any) => n.id === edge.source);
      if (sourceNode?.data?.type === 'ab_test') {
        const nodeAbStats = abTestStats[edge.source];
        // Extrai índice da variante do sourceHandle (ex: variant_0 -> 0)
        const variantIndex = parseInt(edge.sourceHandle?.replace('variant_', '') || '0');
        const variantLabels = ['A', 'B', 'C', 'D'];
        const variantLabel = variantLabels[variantIndex] || `V${variantIndex}`;
        const variantStats = nodeAbStats?.variants[variantLabel];

        return {
          ...edge,
          type: 'abtest',
          data: {
            ...edge.data,
            stats: getEdgeStats(edge.source, edge.target),
            variant: variantLabel,
            count: variantStats?.count || 0,
            percentage: variantStats?.percentage || 0,
            total: nodeAbStats?.total || 0,
          },
        };
      }

      // Edges padrão: injeta stats do funil + entries do node de origem para contexto
      return {
        ...edge,
        // Força tipo 'default' para garantir uso do FunnelEdge com badges de métricas
        type: 'default',
        data: {
          ...edge.data,
          stats: getEdgeStats(edge.source, edge.target),
          sourceEntries: getNodeStats(edge.source)?.entries ?? 0,
        },
      };
    });
  }, [flowData?.edges, flowData?.nodes, getEdgeStats, getNodeStats, abTestStats]);

  // Ranking dos top nodes com maior drop-off
  const bottleneckNodes = useMemo(() => {
    return [...nodeStats]
      .filter((n) => n.entries > 0 && n.drop_offs > 0)
      .sort((a, b) => (b.drop_offs / b.entries) - (a.drop_offs / a.entries))
      .slice(0, 5);
  }, [nodeStats]);

  // Encontra label do node pelo ID
  const getNodeLabel = useCallback(
    (nodeId: string): string => {
      const node = flowData?.nodes?.find((n: any) => n.id === nodeId);
      // Prioriza o apelido (nickname) definido pelo usuário sobre o label técnico
      const nickname = (node?.data as any)?.config?.nickname;
      return nickname || (node?.data?.label as string) || nodeId;
    },
    [flowData?.nodes],
  );

  /**
   * Handler de clique no node: abre modal de drill-down com sessões filtradas
   */
  const handleNodeClick = useCallback(
    (_event: MouseEvent, node: Node) => {
      const stats = (node.data as any)?.funnelStats;
      const entries = stats?.entries ?? 0;
      setDrilldownNodeId(node.id);
      setDrilldownNodeLabel(getNodeLabel(node.id));
      setDrilldownEntries(entries);
    },
    [getNodeLabel],
  );

  // Label do período selecionado
  const selectedPeriod = PERIOD_OPTIONS.find((o) => o.value === period);

  if (isLoadingFlow) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Carregando estrutura do fluxo...
      </div>
    );
  }

  return (
    <div className="flex h-full gap-0">
      {/* Canvas principal */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodesWithStats}
          edges={edgesWithStats}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          onNodeClick={handleNodeClick}
          panOnDrag
          zoomOnScroll
          minZoom={0.3}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            className="!bg-card !border-border"
            nodeColor="hsl(var(--muted-foreground))"
            maskColor="hsl(var(--background) / 0.7)"
          />

          {/* Filtro de período no topo */}
          <Panel position="top-left">
            <div className="flex items-center gap-2">
              <Popover open={periodOpen} onOpenChange={setPeriodOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 gap-2 bg-card shadow-sm">
                    <CalendarIcon className="h-3.5 w-3.5 opacity-70" />
                    <span className="text-xs">{selectedPeriod?.short || '7 dias'}</span>
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" align="start">
                  <Command>
                    <CommandList>
                      <CommandGroup heading="Período">
                        {PERIOD_OPTIONS.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => {
                              setPeriod(option.value);
                              setPeriodOpen(false);
                            }}
                            className="cursor-pointer"
                          >
                            <div
                              className={cn(
                                'mr-2 h-3.5 w-3.5 rounded-full border flex items-center justify-center',
                                period === option.value
                                  ? 'border-primary bg-primary'
                                  : 'border-muted',
                              )}
                            >
                              {period === option.value && (
                                <Check className="h-2 w-2 text-primary-foreground" />
                              )}
                            </div>
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {isLoadingStats && (
                <Badge variant="secondary" className="text-[10px] animate-pulse">
                  Atualizando...
                </Badge>
              )}
            </div>
          </Panel>

          {/* Resumo rápido no topo direito */}
          <Panel position="top-right">
            <div className="flex items-center gap-2">
              <div className="bg-card border border-border rounded-md px-3 py-1.5 shadow-sm flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="h-3 w-3" />
                  {nodeStats.length > 0
                    ? Math.max(...nodeStats.map((n) => n.entries)).toLocaleString('pt-BR')
                    : '0'}{' '}
                  leads
                </span>
                <span className="text-border">|</span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <TrendingDown className="h-3 w-3" />
                  {nodeStats.reduce((sum, n) => sum + n.drop_offs, 0).toLocaleString('pt-BR')}{' '}
                  drop-offs
                </span>
              </div>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Botão toggle do painel lateral (sempre visível na borda direita) */}
      <button
        onClick={() => setInsightsOpen((prev) => !prev)}
        className="w-6 border-l border-border bg-card hover:bg-accent flex items-center justify-center transition-colors shrink-0"
        title={insightsOpen ? 'Retrair painel' : 'Expandir insights'}
      >
        {insightsOpen ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </button>

      {/* Painel lateral de insights - retrátil com transição */}
      <div
        className={cn(
          'border-l border-border bg-card overflow-hidden flex flex-col transition-all duration-300',
          insightsOpen ? 'w-72' : 'w-0 border-l-0'
        )}
      >
        <div className="p-3 border-b border-border min-w-[288px]">
          <h3 className="text-sm font-semibold text-foreground">Insights do Funil</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Gargalos e métricas por node
          </p>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 space-y-3">
            {/* Top gargalos */}
            <Card className="border-border">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-destructive" />
                  Top Gargalos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1 space-y-2">
                {bottleneckNodes.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">Sem dados suficientes</p>
                ) : (
                  bottleneckNodes.map((node, index) => {
                    const dropRate = node.entries > 0 
                      ? ((node.drop_offs / node.entries) * 100).toFixed(1) 
                      : '0';
                    return (
                      <div
                        key={node.node_id}
                        className="flex items-center gap-2 text-[11px]"
                      >
                        <span className="text-muted-foreground font-mono w-4">
                          #{index + 1}
                        </span>
                        <span className="flex-1 truncate text-foreground">
                          {getNodeLabel(node.node_id)}
                        </span>
                        <Badge variant="destructive" className="text-[9px] px-1.5 py-0">
                          -{dropRate}%
                        </Badge>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Tempo médio por node */}
            <Card className="border-border">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Tempo Médio por Node
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1 space-y-2">
                {nodeStats
                  .filter((n) => n.avg_time_seconds && n.avg_time_seconds > 0)
                  .sort((a, b) => (b.avg_time_seconds || 0) - (a.avg_time_seconds || 0))
                  .slice(0, 5)
                  .map((node) => {
                    const time = node.avg_time_seconds || 0;
                    const timeLabel = time < 60
                      ? `${Math.round(time)}s`
                      : `${Math.floor(time / 60)}m ${Math.round(time % 60)}s`;
                    return (
                      <div
                        key={node.node_id}
                        className="flex items-center gap-2 text-[11px]"
                      >
                        <span className="flex-1 truncate text-foreground">
                          {getNodeLabel(node.node_id)}
                        </span>
                        <span className="text-muted-foreground font-mono">
                          {timeLabel}
                        </span>
                      </div>
                    );
                  })}
                {nodeStats.filter((n) => n.avg_time_seconds && n.avg_time_seconds > 0).length === 0 && (
                  <p className="text-[11px] text-muted-foreground">Sem dados de tempo</p>
                )}
              </CardContent>
            </Card>

            {/* Estatísticas gerais por node */}
            <Card className="border-border">
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Todos os Nodes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-1 space-y-1.5">
                {nodeStats
                  .sort((a, b) => b.entries - a.entries)
                  .map((node) => (
                    <div
                      key={node.node_id}
                      className="flex items-center gap-2 text-[11px]"
                    >
                      <span className="flex-1 truncate text-foreground">
                        {getNodeLabel(node.node_id)}
                      </span>
                      <span className="text-muted-foreground font-mono">
                        {node.entries}
                      </span>
                      <span
                        className={cn(
                          'font-mono text-[10px]',
                          node.funnel_percentage >= 70 && 'text-green-600 dark:text-green-400',
                          node.funnel_percentage >= 30 && node.funnel_percentage < 70 && 'text-yellow-600 dark:text-yellow-400',
                          node.funnel_percentage < 30 && 'text-red-600 dark:text-red-400',
                        )}
                      >
                        {node.funnel_percentage.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                {nodeStats.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    Nenhuma execução registrada neste período
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </div>

      {/* Modal de drill-down por node */}
      {drilldownNodeId && (
        <FunnelNodeDrilldownModal
          open={!!drilldownNodeId}
          onOpenChange={(open) => { if (!open) setDrilldownNodeId(null); }}
          flowId={flowId}
          nodeId={drilldownNodeId}
          nodeLabel={drilldownNodeLabel}
          entries={drilldownEntries}
          period={period}
        />
      )}
    </div>
  );
}
